import React, { useState, useEffect, useRef } from 'react';
import { Typography, TextField, Button, Card, CardContent, Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import config from '../config/secure-keys.json';

const openaiKey = config.openaiKey;
const elevenlabsKey = config.elevenlabsKey;
const voiceId = config.voiceId;

const gradeOptions = ['A', 'A-', 'B', 'B-', 'C', 'C-', 'D'];

const ConversationReview = () => {
  const [rawText, setRawText] = useState('');
  const [parsedSummary, setParsedSummary] = useState('');
  const [evaluationData, setEvaluationData] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [grade, setGrade] = useState('');
  const [competentScenarios, setCompetentScenarios] = useState([]);
  const [strengths, setStrengths] = useState([]);
  const [coaching, setCoaching] = useState([]);
  const [isTalking, setIsTalking] = useState(false);
  const audioRef = useRef();

  const parseText = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${openaiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are a parser that extracts a brief summary, a list of evaluation criteria, a grade, caregiving scenarios the trainee is ready for, strengths, and 1-2 coaching tips. Return JSON as: { "summary": "...", "criteria": [{"criteria": "Empathy", "score": 7}, ...], "grade": "A", "competentScenarios": ["de-escalation"], "strengths": ["kind tone"], "coaching": ["respond more directly"] }'
            },
            {
              role: 'user',
              content: rawText
            }
          ]
        })
      });
      const data = await response.json();
      const result = data.choices[0].message.content;
      const parsed = JSON.parse(result);
      setParsedSummary(parsed.summary);
      setEvaluationData(parsed.criteria || []);
      setGrade(parsed.grade);
      setCompetentScenarios(parsed.competentScenarios);
      setStrengths(parsed.strengths);
      setCoaching(parsed.coaching);
    } catch (err) {
      console.error('Parsing failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!parsedSummary) return;
    const generateAudio = async () => {
      try {
        const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
          method: 'POST',
          headers: {
            'xi-api-key': elevenlabsKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: `This evaluation focused on a simulated interaction between a trainee and an elder. The trainee demonstrated a warm and respectful approach, essential for dementia care. Strengths included empathy and clarity. Opportunities for growth include emotional validation and responsiveness. Alzheimer's Association best practices stress calm tone, listening, and gently redirecting when needed. Based on performance, the trainee shows readiness in basic scenarios like reassurance and redirection, with room to grow in managing confusion and repetition.`
          })
        });
        const blob = await response.blob();
        const audioURL = URL.createObjectURL(blob);
        setAudioUrl(audioURL);
      } catch (err) {
        console.error('Audio generation failed:', err);
      }
    };

    generateAudio();
  }, [parsedSummary]);

  const handleExport = () => {
    const doc = new jsPDF();
    doc.text('Conversation Summary', 14, 20);
    doc.setFontSize(11);
    doc.text(parsedSummary, 14, 30);

    const rows = evaluationData.length > 0
      ? evaluationData.map(item => [item.criteria, item.score])
      : [['No criteria data available', '']];

    autoTable(doc, {
      startY: 40,
      head: [['Criteria', 'Score']],
      body: rows
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Grade', 'Scenarios', 'Strengths', 'Coaching']],
      body: [[
        grade,
        competentScenarios.join(', '),
        strengths.join(', '),
        coaching.join('; ')
      ]]
    });

    doc.save('conversation_review.pdf');
  };

  return (
    <Box p={4} display="grid" gap={4}>
      <Card>
        <CardContent>
          <Typography variant="h5">Paste Evaluation Text</Typography>
          <TextField
            label="Raw Text Input"
            multiline
            fullWidth
            minRows={10}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button variant="contained" color="primary" onClick={parseText} sx={{ mt: 2 }} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Analyze'}
          </Button>
        </CardContent>
      </Card>

      {parsedSummary && (
        <>
          <Card>
            <CardContent>
              <Typography variant="h6">Conversation Summary</Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>{parsedSummary}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Evaluation Criteria</Typography>
              <Box sx={{ width: '100%', height: 500 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={evaluationData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="criteria" />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} />
                    <Radar name="Score" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Grade Overview</Typography>
              <Box display="flex" justifyContent="space-around" alignItems="center" sx={{ mt: 2 }}>
                {gradeOptions.map(g => (
                  <Box key={g} textAlign="center">
                    <Typography variant="subtitle2" color={g === grade ? 'primary' : 'textSecondary'}>
                      {g}
                    </Typography>
                    <Box width={20} height={20} borderRadius={2} bgcolor={g === grade ? 'primary.main' : 'grey.300'} mx="auto" />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6">Caregiving Scenarios & Coaching</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Strengths</TableCell>
                    <TableCell>Ready For</TableCell>
                    <TableCell>Coaching Tips</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{strengths.join(', ')}</TableCell>
                    <TableCell>{competentScenarios.join(', ')}</TableCell>
                    <TableCell>{coaching.join('; ')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Button variant="outlined" onClick={handleExport} sx={{ mt: 2 }}>Download PDF</Button>
            </CardContent>
          </Card>

          {audioUrl && (
            <Card>
              <CardContent>
                <Typography variant="h6">Audio Summary</Typography>
                <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                  <Box
                    component="img"
                    src="/images/rdimg2.jpg"
                    alt="Avatar"
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      transition: 'transform 0.3s ease-in-out',
                      transform: isTalking ? 'scale(1.05)' : 'scale(1)'
                    }}
                  />
                  <audio
                    ref={audioRef}
                    controls
                    src={audioUrl}
                    style={{ width: '100%', marginTop: 16 }}
                    onPlay={() => setIsTalking(true)}
                    onPause={() => setIsTalking(false)}
                    onEnded={() => setIsTalking(false)}
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default ConversationReview;

